import * as React from 'react';

import {
  Checkbox,
  cn,
  RawTooltip,
  Tooltip,
  TooltipTrigger,
} from '@rematter/ui-web';
import { LucideChevronUp, LucideInfo } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';

import { moduleIcons } from '../../_generated/permissions';
import type {
  EntityPermission,
  ModulePermissions,
  Permission,
} from '../../_generated/permissions/types';

// =============================================================================
// Types
// =============================================================================

export interface PermissionsEditorProps {
  /** Array of module permission definitions */
  modules: ModulePermissions[];
  /** Current permission state - maps permission key to enabled/disabled */
  value: Record<string, boolean>;
  /** Callback when a single permission changes */
  onChange?: (permissionKey: string, enabled: boolean) => void;
  /** Callback for bulk permission changes */
  onBulkChange?: (changes: Record<string, boolean>) => void;
  /** Whether the editor is in read-only mode */
  readOnly?: boolean;
  /** Tooltip message shown in read-only mode */
  readOnlyMessage?: string;
  /** Whether the permissions section can be collapsed */
  collapsible?: boolean;
  /** Whether the permissions section starts expanded (only used when collapsible is true) */
  defaultExpanded?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// =============================================================================
// Constants
// =============================================================================

const CRUD_ACTIONS = ['Read', 'Create', 'Update', 'Delete'] as const;
type CrudAction = (typeof CRUD_ACTIONS)[number];

const CRUD_LABELS: Record<CrudAction, string> = {
  Read: 'R',
  Create: 'W',
  Update: 'U',
  Delete: 'D',
};

// =============================================================================
// Helper Functions
// =============================================================================

function getPermissionKeysForAction(
  modules: ModulePermissions[],
  action: CrudAction,
): string[] {
  return modules.flatMap((module) =>
    module.entities.flatMap((entity) =>
      entity.permissions.filter((p) => p.action === action).map((p) => p.key),
    ),
  );
}

function getColumnState(
  permissionKeys: string[],
  value: Record<string, boolean>,
): 'checked' | 'unchecked' | 'indeterminate' {
  if (permissionKeys.length === 0) return 'unchecked';
  const checkedCount = permissionKeys.filter((key) => value[key]).length;
  if (checkedCount === 0) return 'unchecked';
  if (checkedCount === permissionKeys.length) return 'checked';
  return 'indeterminate';
}

// =============================================================================
// Sub-components
// =============================================================================

interface PermissionsHeaderProps {
  modules: ModulePermissions[];
  value: Record<string, boolean>;
  onBulkChange?: (changes: Record<string, boolean>) => void;
  readOnly?: boolean;
  readOnlyMessage?: string;
  collapsible?: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

const PermissionsHeader: React.FC<PermissionsHeaderProps> = ({
  modules,
  value,
  onBulkChange,
  readOnly,
  readOnlyMessage,
  collapsible,
  isExpanded,
  onToggleExpanded,
}) => {
  const handleGlobalToggle = React.useCallback(
    (action: CrudAction) => {
      if (readOnly || !onBulkChange) return;
      const keys = getPermissionKeysForAction(modules, action);
      const state = getColumnState(keys, value);
      const newValue = state !== 'checked';
      const changes: Record<string, boolean> = {};
      keys.forEach((key) => {
        changes[key] = newValue;
      });
      onBulkChange(changes);
    },
    [modules, value, onBulkChange, readOnly],
  );

  const headerColumns: { action: CrudAction; label: string }[] = [
    { action: 'Read', label: 'Read' },
    { action: 'Create', label: 'Write' },
    { action: 'Delete', label: 'Delete' },
  ];

  return (
    <div className="@PermissionsHeader flex items-center py-2.5">
      <div className="flex w-[240px] shrink-0 items-center gap-1.5 pr-4">
        {collapsible && (
          <button
            type="button"
            className="flex size-5 items-center justify-center rounded bg-neutral/10"
            onClick={onToggleExpanded}
            aria-label={
              isExpanded ? 'Collapse permissions' : 'Expand permissions'
            }
          >
            <LucideChevronUp
              size={16}
              className={cn(
                'text-neutral/60 transition-transform',
                !isExpanded && 'rotate-180',
              )}
            />
          </button>
        )}
        <span className="font-bold text-neutral/80">Permissions</span>
        {readOnly && readOnlyMessage && (
          <Tooltip tip={readOnlyMessage}>
            <LucideInfo
              size={14}
              className="text-neutral/40 hover:text-neutral/60"
            />
          </Tooltip>
        )}
      </div>

      {headerColumns.map(({ action, label }) => {
        const keys = getPermissionKeysForAction(modules, action);
        const state = getColumnState(keys, value);

        return (
          <div key={action} className="w-[70px] shrink-0">
            <Checkbox
              isSelected={state === 'checked'}
              isIndeterminate={state === 'indeterminate'}
              isDisabled={readOnly}
              onChange={() => handleGlobalToggle(action)}
              className="text-sm text-neutral/70"
            >
              {label}
            </Checkbox>
          </div>
        );
      })}
    </div>
  );
};

interface PermissionsModuleSectionProps {
  module: ModulePermissions;
  value: Record<string, boolean>;
  onChange?: (permissionKey: string, enabled: boolean) => void;
  readOnly?: boolean;
  readOnlyMessage?: string;
}

const PermissionsModuleSection: React.FC<PermissionsModuleSectionProps> = ({
  module,
  value,
  onChange,
  readOnly,
  readOnlyMessage,
}) => {
  const Icon = moduleIcons[module.module];

  return (
    <div className="@PermissionsModuleSection">
      <div className="my-2 flex items-center gap-1.5 rounded-md border border-neutral/5 bg-[#fbfbfc] px-3 py-2">
        {Icon && <Icon size={16} className="text-primary" />}
        <span className="text-sm font-semibold text-neutral">
          {module.displayName}
        </span>
      </div>

      {module.entities.map((entity) => (
        <PermissionsEntityRow
          key={entity.name}
          entity={entity}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          readOnlyMessage={readOnlyMessage}
        />
      ))}
    </div>
  );
};

interface PermissionsEntityRowProps {
  entity: EntityPermission;
  value: Record<string, boolean>;
  onChange?: (permissionKey: string, enabled: boolean) => void;
  readOnly?: boolean;
  readOnlyMessage?: string;
}

const PermissionsEntityRow: React.FC<PermissionsEntityRowProps> = ({
  entity,
  value,
  onChange,
  readOnly,
  readOnlyMessage,
}) => {
  const crudPermissions = entity.permissions.filter(
    (p) => p.category === 'crud',
  );
  const customPermissions = entity.permissions.filter(
    (p) => p.category === 'custom',
  );

  const crudMap = React.useMemo(() => {
    const map = new Map<CrudAction, Permission | undefined>();
    CRUD_ACTIONS.forEach((action) => {
      map.set(
        action,
        crudPermissions.find((p) => p.action === action),
      );
    });
    return map;
  }, [crudPermissions]);

  const handleChange = React.useCallback(
    (permission: Permission, enabled: boolean) => {
      if (readOnly || !onChange) return;
      onChange(permission.key, enabled);
    },
    [onChange, readOnly],
  );

  const showTooltip = readOnly && !!readOnlyMessage;
  const displayColumns: CrudAction[] = ['Read', 'Create', 'Delete'];

  const rowContent = (
    <>
      <span className="w-[240px] shrink-0 truncate pr-4 pl-3 text-left text-sm text-neutral/80">
        {entity.displayName}
      </span>

      {displayColumns.map((action) => {
        const permission = crudMap.get(action);
        const label = CRUD_LABELS[action];

        if (!permission) {
          return (
            <div key={action} className="w-[70px] shrink-0">
              <span className="text-sm text-neutral/30">-</span>
            </div>
          );
        }

        return (
          <div key={action} className="w-[70px] shrink-0">
            <Checkbox
              isSelected={value[permission.key] ?? false}
              isDisabled={readOnly}
              onChange={(checked) => handleChange(permission, checked)}
              className="text-sm text-neutral/60"
            >
              {label}
            </Checkbox>
          </div>
        );
      })}

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3.5 gap-y-2.5">
        {customPermissions.map((permission) => (
          <Checkbox
            key={permission.key}
            isSelected={value[permission.key] ?? false}
            isDisabled={readOnly}
            onChange={(checked) => handleChange(permission, checked)}
            className="w-auto text-sm text-neutral/60"
          >
            {permission.action}
          </Checkbox>
        ))}
      </div>
    </>
  );

  if (showTooltip) {
    return (
      <TooltipTrigger delay={500}>
        <AriaButton className="flex w-full cursor-default items-center border-none bg-transparent py-2.5 outline-none">
          {rowContent}
        </AriaButton>
        <RawTooltip className="bg-neutral text-white">
          {readOnlyMessage}
        </RawTooltip>
      </TooltipTrigger>
    );
  }

  return (
    <div className="@PermissionsEntityRow flex items-center py-2.5">
      {rowContent}
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const PermissionsEditor: React.FC<PermissionsEditorProps> = ({
  modules,
  value,
  onChange,
  onBulkChange,
  readOnly = false,
  readOnlyMessage = "Role permissions can't be changed at the facility level. To edit, go to Role Settings.",
  collapsible = false,
  defaultExpanded = true,
  className,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  const handleToggleExpanded = React.useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const showContent = !collapsible || isExpanded;

  return (
    <div className={cn('@PermissionsEditor flex flex-col', className)}>
      <PermissionsHeader
        modules={modules}
        value={value}
        onBulkChange={onBulkChange}
        readOnly={readOnly}
        readOnlyMessage={readOnlyMessage}
        collapsible={collapsible}
        isExpanded={isExpanded}
        onToggleExpanded={handleToggleExpanded}
      />

      {showContent && (
        <div className="flex-1 overflow-y-auto">
          {modules.map((module) => (
            <PermissionsModuleSection
              key={module.module}
              module={module}
              value={value}
              onChange={onChange}
              readOnly={readOnly}
              readOnlyMessage={readOnly ? readOnlyMessage : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};
